import {
  isQuestionnaireScoreCalculation,
  QUESTIONNAIRE_SCORE_CALCULATIONS,
  type QuestionnaireScoreCalculation,
  scoreCalculation,
  scoreItemIneligibility,
  scoreItemsRange,
} from "@repo/domain";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { InputField, NumberField, SelectField } from "@repo/ui/form-fields";
import { cn } from "@repo/ui/lib/utils";
import { Plus, Trash2, X } from "lucide-react";
import { AddCatalogItemButton } from "@/shared/common/add-catalog-item-button";
import { referenceKey, temporaryOrPersisted } from "./condition-references";
import { SubscoreQuestionPicker } from "./subscore-question-picker";
import { type DraftBand, type DraftItem, type DraftScore, type DraftSection, draftTotalScore } from "./types";
import { generateLinkId } from "./utils/link-id";

function candidates(sections: DraftSection[], performance: boolean) {
  return sections.flatMap((section) =>
    section.items.flatMap((item) => {
      const describe = (item: DraftItem, repeating: boolean) => {
        const reason = scoreItemIneligibility(item, section, repeating, performance);
        return {
          item,
          reference: temporaryOrPersisted(item),
          label: item.kind === "Display" ? item.text : item.label,
          reason,
          ...scoreItemsRange([item]),
        };
      };
      return item.kind === "RepeatingGroup"
        ? item.items.map((child) => describe(child, true))
        : [describe(item, false)];
    }),
  );
}

function coverage(from: number, to: number): string {
  if (to < from) return "Empty";
  return to === from ? String(from) : `${from}–${to}`;
}

const BAND_ROW = "grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_3.5rem_1.75rem] items-center gap-1.5";

/** An optional inclusive end leaves uninterpreted gaps between bands. */
function BandsEditor({
  scoreLabel,
  minimum,
  maximum,
  bands,
  onChange,
}: {
  scoreLabel: string;
  minimum: number;
  maximum: number;
  bands: DraftBand[];
  onChange: (bands: DraftBand[]) => void;
}) {
  const update = (index: number, band: DraftBand) => onChange(bands.map((prior, i) => (i === index ? band : prior)));
  const last = bands.at(-1);
  return (
    <fieldset className="space-y-1.5" aria-label={`Bands for ${scoreLabel}`}>
      {bands.length > 0 && (
        <div className={`${BAND_ROW} text-xs text-muted-foreground`} aria-hidden>
          <span>Band label</span>
          <span>From</span>
          <span>To (optional)</span>
          <span>Covers</span>
        </div>
      )}
      {bands.map((band, index) => (
        <div key={band.clientId} className={BAND_ROW}>
          <InputField
            ariaLabel="Band label"
            value={band.label}
            onChange={(label) => update(index, { ...band, label })}
            onBlur={() => {}}
          />
          <NumberField
            ariaLabel="From"
            value={band.from}
            step={1}
            onChange={(from) => update(index, { ...band, from: from ?? 0 })}
            onBlur={() => {}}
          />
          <NumberField
            ariaLabel="To (optional)"
            value={band.to ?? null}
            step={1}
            onChange={(to) => update(index, { ...band, to: to ?? undefined })}
            onBlur={() => {}}
          />
          <span className="text-xs tabular-nums text-muted-foreground">
            {coverage(band.from, band.to ?? (bands[index + 1]?.from ?? maximum + 1) - 1)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0 text-destructive hover:text-destructive"
            aria-label={`Remove band ${band.label}`}
            onClick={() => onChange(bands.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 px-2 text-xs text-muted-foreground"
        aria-label={`Add Band to ${scoreLabel || "score"}`}
        onClick={() =>
          onChange([
            ...bands,
            {
              clientId: generateLinkId(),
              label: "",
              from: last ? (last.to ?? last.from) + 1 : Number.isFinite(minimum) ? minimum : 0,
            },
          ])
        }
      >
        <Plus className="size-3" />
        Add band
      </Button>
    </fieldset>
  );
}

const CALCULATION_WORDING: Record<QuestionnaireScoreCalculation | "sum", { label: string; description?: string }> = {
  sum: { label: "Sum" },
  "percent-change": {
    label: "Percent change",
    description: "(second − first) ÷ first × 100. The first task is the baseline.",
  },
};

const CALCULATION_OPTIONS = [
  { value: "sum", label: CALCULATION_WORDING.sum.label },
  ...QUESTIONNAIRE_SCORE_CALCULATIONS.map((value) => ({ value, label: CALCULATION_WORDING[value].label })),
];

/** Presentational: the builder owns every staged score edit. */
export function ScoresPanel({
  performance = false,
  sections,
  scores,
  totalBands,
  onChange,
  onTotalBandsChange,
}: {
  performance?: boolean;
  sections: DraftSection[];
  scores: DraftScore[];
  totalBands: DraftBand[];
  onChange: (scores: DraftScore[]) => void;
  onTotalBandsChange: (bands: DraftBand[]) => void;
}) {
  const items = candidates(sections, performance);
  const total = draftTotalScore(sections, performance, scores);
  const { maximum: totalMaximum, minimum: totalMinimum } = scoreItemsRange(
    sections.flatMap((section) => section.items).filter((item) => total?.itemIds.includes(item.clientId)),
  );
  const update = (index: number, score: DraftScore) =>
    onChange(scores.map((prior, i) => (i === index ? score : prior)));
  const range = (score: DraftScore) => {
    const selected = new Set(score.items.map(referenceKey));
    const chosen = items.filter((item) => selected.has(referenceKey(item.reference)));
    return {
      selected,
      ...scoreItemsRange(
        chosen.map(({ item }) => item),
        score.calculation,
      ),
    };
  };
  return (
    <>
      <section className="mt-8 space-y-3" aria-label="Score bands">
        <h2 className="text-sm font-medium text-muted-foreground">Score bands</h2>
        {total && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <span>Total</span>
              <Badge variant="secondary">{Number.isFinite(totalMaximum) ? `Max ${totalMaximum}` : "Unbounded"}</Badge>
            </div>
            <BandsEditor
              scoreLabel="Total"
              minimum={totalMinimum}
              maximum={totalMaximum}
              bands={totalBands}
              onChange={onTotalBandsChange}
            />
          </div>
        )}
        {scores.map((score, index) => {
          if (!scoreCalculation(score.calculation).allowsBands) return null;
          const { minimum, maximum } = range(score);
          return (
            <div key={score.clientId} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className={cn(!score.label && "text-muted-foreground")}>{score.label || "Untitled score"}</span>
                <Badge variant="secondary">{Number.isFinite(maximum) ? `Max ${maximum}` : "Unbounded"}</Badge>
              </div>
              <BandsEditor
                scoreLabel={score.label}
                minimum={minimum}
                maximum={maximum}
                bands={score.bands ?? []}
                onChange={(bands) => update(index, { ...score, bands })}
              />
            </div>
          );
        })}
      </section>
      <section className="mt-8 space-y-3" aria-label="Subscores">
        <h2 className="text-sm font-medium text-muted-foreground">
          Subscores
          {scores.length > 0 && ` (${scores.length})`}
        </h2>
        {scores.map((score, index) => {
          const { selected, maximum } = range(score);
          return (
            <div key={score.clientId} className="group space-y-2 rounded-md border p-3">
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <InputField
                    label="Score label"
                    value={score.label}
                    onChange={(label) => update(index, { ...score, label })}
                    onBlur={() => {}}
                  />
                </div>
                {performance && (
                  <div className="w-40 shrink-0">
                    <SelectField
                      label="Calculation"
                      options={CALCULATION_OPTIONS}
                      value={score.calculation ?? "sum"}
                      onChange={(value) => {
                        const { calculation: _, ...rest } = score;
                        update(
                          index,
                          isQuestionnaireScoreCalculation(value)
                            ? {
                                ...rest,
                                calculation: value,
                                bands: scoreCalculation(value).allowsBands ? rest.bands : [],
                              }
                            : rest,
                        );
                      }}
                      onBlur={() => {}}
                    />
                  </div>
                )}
                <div className="flex h-9 shrink-0 items-center gap-1">
                  <Badge variant="secondary" className="px-1.5 py-0 text-xs text-muted-foreground">
                    {scoreCalculation(score.calculation).unit
                      ? scoreCalculation(score.calculation).unit?.display
                      : Number.isFinite(maximum)
                        ? `Max ${maximum}`
                        : "Unbounded"}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="text-destructive opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label={`Remove score ${score.label}`}
                    onClick={() => onChange(scores.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </div>
              </div>
              {CALCULATION_WORDING[score.calculation ?? "sum"].description && (
                <p className="px-2 text-xs text-muted-foreground">
                  {CALCULATION_WORDING[score.calculation ?? "sum"].description}
                </p>
              )}
              {score.items.length === 0 ? (
                <p className="px-2 text-xs text-muted-foreground">No questions yet</p>
              ) : (
                <ul className="space-y-0.5">
                  {score.items.map((reference) => {
                    const key = referenceKey(reference);
                    const label = items.find((item) => referenceKey(item.reference) === key)?.label ?? key;
                    return (
                      <li key={key} className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/50">
                        <span className="min-w-0 flex-1 truncate">{label}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${label} from ${score.label || "score"}`}
                          onClick={() =>
                            update(index, { ...score, items: score.items.filter((ref) => referenceKey(ref) !== key) })
                          }
                        >
                          <X className="size-3" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {score.items.length < (scoreCalculation(score.calculation).maximumItems ?? Infinity) && (
                <SubscoreQuestionPicker
                  scoreLabel={score.label}
                  candidates={items
                    .filter((item) => !selected.has(referenceKey(item.reference)))
                    .map((item) => ({
                      key: referenceKey(item.reference),
                      reference: item.reference,
                      label: item.label,
                      reason: item.reason,
                    }))}
                  onPick={(reference) => update(index, { ...score, items: [...score.items, reference] })}
                />
              )}
            </div>
          );
        })}
        <AddCatalogItemButton
          label="Subscore"
          onClick={() => onChange([...scores, { clientId: generateLinkId(), label: "", items: [] }])}
        />
      </section>
    </>
  );
}
